import { Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/format";

const fmtQty = (n) => (n == null ? "0" : Number(n).toLocaleString("cs-CZ", { maximumFractionDigits: 3 }));

const MOV_LABEL = { receipt: "Příjem", issue: "Výdej", return: "Vratka", transfer: "Převod", writeoff: "Odpis", adjustment: "Inventura", receipt_storno: "Storno příjmu" };
const MOV_CLS = {
  receipt: "text-emerald-500 bg-emerald-500/10",
  issue: "text-orange-500 bg-orange-500/10",
  return: "text-sky-500 bg-sky-500/10",
  transfer: "text-violet-500 bg-violet-500/10",
  writeoff: "text-rose-500 bg-rose-500/10",
  adjustment: "text-amber-500 bg-amber-500/10",
  receipt_storno: "text-muted-foreground bg-secondary",
};

function movTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const now = new Date();
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const hhmm = d.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });
  if (sameDay(d, now)) return `dnes ${hhmm}`;
  if (sameDay(d, y)) return `včera ${hhmm}`;
  return d.toLocaleDateString("cs-CZ");
}

function target(m) {
  if (m.type === "issue") return m.project_name || "Přímá spotřeba";
  if (m.type === "writeoff") return m.project_name || "Režie firmy";
  if (m.type === "transfer") return m.to_warehouse_name || "jiný sklad";
  if (m.type === "receipt") return m.note || null;
  if (m.type === "return") return m.project_name || null;
  return null;
}

// Tab „Pohyby" (§7). Vratka jen u výdejů s returnable > 0 na skladě, kde smíš operovat.
export function MovementList({ movements, showWarehouse, money, canOp, onReturn }) {
  if (!movements.length) return <div className="p-8 text-center text-muted-foreground text-sm" data-testid="movements-empty">Zatím žádné pohyby.</div>;
  return (
    <div className="divide-y divide-border" data-testid="movement-list">
      {movements.map((m) => {
        const tgt = target(m);
        const canReturn = m.type === "issue" && (m.returnable || 0) > 0.0001 && (canOp ? canOp(m.warehouse_id) : true);
        return (
          <div key={m.id} className="px-4 py-3 flex items-center justify-between gap-3 text-sm" data-testid={`movement-${m.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${MOV_CLS[m.type]}`}>{MOV_LABEL[m.type]}</span>
                <span className="font-semibold truncate">{m.name}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 break-words">
                {showWarehouse && m.from_warehouse_name && <>{m.from_warehouse_name} · </>}
                {movTime(m.created_at)} · {fmtQty(m.qty)} {m.unit}
                {money && m.total != null && <> · {formatMoney(m.total)}</>}
                {tgt && <> · {(m.type === "transfer" || m.type === "issue" || m.type === "writeoff") ? "→ " : ""}{tgt}</>}
                {m.received_by_name && <> · převzal {m.received_by_name}</>}
                {m.user_name && <> · vydal {m.user_name}</>}
              </div>
            </div>
            {canReturn && (
              <Button size="sm" variant="ghost" className="shrink-0" onClick={() => onReturn(m)} data-testid={`return-btn-${m.id}`}>
                <Undo2 size={15} className="mr-1" /> Vratka
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
